import { useCallback, useEffect, useState } from 'react'
import { Plus, Search } from 'lucide-react'
import { useParams } from 'react-router'

import type { SponsorshipResponse } from '@/api/types/Sponsorship'
import {
  createIndividualSponsorship,
  updateIndividualSponsorship,
  useGetIndividualSponsorships
} from '@/api/organizationIndividual'
import Button from '@/elements/Button'
import Flex from '@/elements/Flex'
import FormInput from '@/elements/FormInput'
import Text from '@/elements/Text'
import Loading from '@/elements/Loading'
import Table from '@/components/Table'
import Modal from '@/components/Modal'
import Pagination from '@/components/Pagination'
import {
  useIndividualSponsorshipTable,
  type IndividualSponsorshipTableType
} from '@/hooks/tables/individualSponsorshipTable'
import useBreakpoints from '@/hooks/useBreakpoints'
import usePagination from '@/hooks/usePagination'
import useFiltering from '@/hooks/useFiltering'
import useIndividualSponsorshipForm from '@/hooks/forms/useIndividualSponsorshipForm'
import toast from '@/utils/toast'

import IndividualSponsorshipForm from './IndividualSponsorshipForm'

function IndividualSponsorships() {
  const { id: individualIdParam } = useParams<{ id: string }>()
  const individualId = individualIdParam || ''

  const [showAddSponsorshipModal, setShowAddSponsorshipModal] = useState(false)
  const [sponsorshipToEdit, setSponsorshipToEdit] = useState<null | SponsorshipResponse>(null)

  const { isTablet, isMobile } = useBreakpoints()
  const { page, limit, setPage } = usePagination(5)
  const { updateFilterBy, allFilters } = useFiltering<['name']>()
  const {
    data: individualSponsorships,
    refetch: refetchSponsorships,
    isLoading: isLoadingSponsorships
  } = useGetIndividualSponsorships({
    subjectId: individualId || '',
    page,
    limit,
    filterBy: allFilters.name
  })
  const sponsorshipTableData = useIndividualSponsorshipTable(
    individualSponsorships?.data?.items || [],
    (sponsorship: SponsorshipResponse) => {
      setSponsorshipToEdit(sponsorship)
      setShowAddSponsorshipModal(true)
    }
  )
  const { form, resetForm } = useIndividualSponsorshipForm(sponsorshipToEdit || null)

  useEffect(() => {
    if (sponsorshipToEdit) {
      resetForm(sponsorshipToEdit)
    }
  }, [resetForm, sponsorshipToEdit])

  const handleUpdateSponsorships = useCallback(async () => {
    const isValid = await form.trigger()
    if (isValid && individualId) {
      const values = form.getValues()
      const body = {
        name: values.name,
        url: values.url,
        beginningDate: values.beginningDate ? values.beginningDate.toISOString() : null,
        endDate: values.endDate ? values.endDate.toISOString() : null,
      }

      try {
        if (sponsorshipToEdit) {
          await updateIndividualSponsorship(individualId, sponsorshipToEdit.id, body)
          toast.success('Sponsorship updated successfully!')
        } else {
          await createIndividualSponsorship(individualId, body)
          toast.success('Sponsorship added successfully!')
        }
        refetchSponsorships()
        setShowAddSponsorshipModal(false)
        setSponsorshipToEdit(null)
        resetForm(null)
      } catch {
        toast.error('Failed to update sponsorships')
      }
    }
  }, [form, individualId, refetchSponsorships, resetForm, sponsorshipToEdit])

  return (
    <Flex
      wfull
      gap={8}
      className='card'
      p={isMobile ? 3 : isTablet ? 5 : 7}
    >
      <Flex gap={2} wfull>
        <Text variant='h3'>Sponsorships</Text>
        <Text color='black-light-40'>
          Revise the individual's sponsorship information.
        </Text>
      </Flex>
      <Flex wfull gap={4} align='center'>
        <Flex inline wfull justify='space-between' align='end' gap={3} wrap>
          <Flex inline align='end' gap={3} gv={2} wfull={isMobile} wrap>
            <FormInput
              name='search'
              placeholder='Search Brand'
              Icon={Search}
              value={allFilters.name || ''}
              onChangeDebounced={q => updateFilterBy('name', q)}
            />
          </Flex>
          <Button
            variant='primary'
            onClick={() => setShowAddSponsorshipModal(true)}
            fullWidth={isMobile}
          >
            <Plus size={18} />
            Add Sponsorship
          </Button>
          <Modal
            title={sponsorshipToEdit ? 'Edit Sponsorship' : 'Add Sponsorship'}
            isOpen={showAddSponsorshipModal}
            onSave={handleUpdateSponsorships}
            onCancel={() => {
              setShowAddSponsorshipModal(false)
              setSponsorshipToEdit(null)
              resetForm(null)
            }}
            disabled={!form.formState.isDirty}
            style={{ width: 538 }}
            preventClickOutsideClose={form.formState.isDirty}
          >
            <IndividualSponsorshipForm {...form} />
          </Modal>
        </Flex>
        {isLoadingSponsorships ? (
          <Flex align='center' justify='center' wfull py={8}>
            <Loading />
          </Flex>
        ) : (
          <>
            <Table<IndividualSponsorshipTableType> {...sponsorshipTableData} />
            <Pagination
              variant={isMobile ? 'compact' : 'default'}
              onPageChange={setPage}
              {...individualSponsorships?.data?.pagination}
            />
          </>
        )}
      </Flex>
    </Flex>
  )
}

export default IndividualSponsorships
